import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import SideTabMenu from "./SideTabMenu";
import {
  Calendar,
  Package,
  Clock,
  Pause,
  X,
  ChevronsRight,
  Play,
  Ban,
  MapPin,
  ChevronsLeft,
} from "lucide-react";
import noproducts from "../../assets/img/default-product.png";
import {
  fetchSubscriptions,
  subscriptionsCancel,
  subscriptionsPause,
  subscriptionsResume,
} from "../../store/thunk/ordersThunk";
import toast from "react-hot-toast";
import PauseSubscriptionModal from "../../shared/modal/PauseSubscriptionModal";
import ResumeSubscriptionModal from "../../shared/modal/ResumeSubscriptionModal";
import CancelSubscriptionModal from "../../shared/modal/CancelSubscriptionModal";
import { encodeToBase64 } from "../../hooks/commonFunction";
import PageLoader from "../../shared/loader/PageLoader";

const SubscriptionsList = () => {
  const dispatch = useDispatch();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 8;

  const loadSubscriptions = async (page = 1) => {
    setLoading(true);
    try {
      const result = await dispatch(
        fetchSubscriptions({ page, limit: itemsPerPage })
      ).unwrap();

      if (result.status === 1 && result?.data) {
        setSubscriptions(result?.data?.subscriptions);

        // Check if API returns total count
        const total = result.data?.pagination?.total;

        if (total !== undefined) {
          setTotalCount(total);
          setTotalPages(result.data?.pagination?.total_pages);
          setCurrentPage(result.data?.pagination?.page);
        }
      } else {
        setSubscriptions([]);
        setTotalCount(0);
        setTotalPages(1);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error("Failed to fetch subscriptions:", error);
      toast.error("Failed to load subscriptions");
      setSubscriptions([]);
      setTotalCount(0);
      setTotalPages(1);
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubscriptions(1);
  }, [dispatch]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      loadSubscriptions(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div>
      <div className="container px-0 lg:px-10 pb-20 lg:pb-10 lg:py-10 md:p-5">
        <div className="grid grid-cols-1 md:grid-cols-12 space-x-0 md:space-x-5">
          <div className="md:col-span-4 lg:col-span-3 hidden md:block">
            <SideTabMenu />
          </div>
          <div className="md:col-span-8 lg:col-span-9 ">
            <div className="md:bg-[#f9f9f9] md:border border-neutral-200 rounded-lg md:shadow-[0_3px_10px_rgb(0,0,0,0.2)]">
              <div className="md:border-b border-gray-200 py-3 px-6 flex items-center">
                <h1 className="text-2xl font-bold text-primary-text whitespace-nowrap">
                  My Subscriptions
                </h1>
                <hr className="w-full h-[1px] bg-neutral-300 ml-3 md:hidden block" />
              </div>
              <div>
                {loading ? (
                  <PageLoader />
                ) : subscriptions.length === 0 ? (
                  <div className="text-center py-20">
                    <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-500 text-lg">
                      No subscriptions found
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-1 gap-1">
                      {subscriptions.map((subscription, index) => (
                        <SubscriptionCard
                          key={index}
                          subscription={subscription}
                          onUpdate={() => loadSubscriptions(currentPage)}
                        />
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                        <div className="text-sm text-gray-700">
                          Showing{" "}
                          <span className="font-medium">
                            {(currentPage - 1) * itemsPerPage + 1}
                          </span>{" "}
                          to{" "}
                          <span className="font-medium">
                            {Math.min(currentPage * itemsPerPage, totalCount)}
                          </span>{" "}
                          of <span className="font-medium">{totalCount}</span>{" "}
                          subscriptions
                        </div>

                        <div className="flex items-center gap-1">
                          {/* Previous Button */}
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`w-10 h-10 flex items-center justify-center rounded-lg border transition-colors ${
                              currentPage === 1
                                ? "border-gray-200 text-gray-400 cursor-not-allowed"
                                : "border-gray-300 text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            <ChevronsLeft className="w-4 h-4" />
                          </button>

                          {/* Page Numbers */}
                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter((page) => {
                              // Show first page, last page, current page, and pages around current
                              return (
                                page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 1 &&
                                  page <= currentPage + 1)
                              );
                            })
                            .map((page, index, array) => {
                              // Add ellipsis if there's a gap
                              const showEllipsisBefore =
                                index > 0 && page - array[index - 1] > 1;

                              return (
                                <React.Fragment key={page}>
                                  {showEllipsisBefore && (
                                    <span className="px-2 text-gray-400">
                                      ...
                                    </span>
                                  )}
                                  <button
                                    onClick={() => handlePageChange(page)}
                                    className={`w-10 h-10 rounded-md border transition-colors ${
                                      currentPage === page
                                        ? "border-primary-100 bg-primary-100 text-white font-semibold"
                                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                                    }`}
                                  >
                                    {page}
                                  </button>
                                </React.Fragment>
                              );
                            })}

                          {/* Next Button */}
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`w-10 h-10 flex items-center justify-center rounded-md border transition-colors ${
                              currentPage === totalPages
                                ? "border-gray-200 text-gray-400 cursor-not-allowed"
                                : "border-gray-300 text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            <ChevronsRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionsList;

const SubscriptionCard = ({ subscription, onUpdate }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Format order type for display
  const formatOrderType = (orderType) => {
    if (orderType === "delivery-partner") {
      return "Delivery Partner";
    }
    return "Default";
  };

  const handleViewDetails = () => {
    navigate(`/subscriptions/${encodeToBase64(subscription.id)}`);
  };

  const handlePauseClick = () => {
    setShowPauseModal(true);
  };

  const handleResumeClick = () => {
    setShowResumeModal(true);
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const confirmPause = async (pause) => {
    setIsSubmitting(true);
    const payload = {
      subscriptionId: subscription.id,
      next_delivery_date: pause?.date,
      ...(pause.reason && { reason: pause.reason }),
    };
    try {
      const result = await dispatch(subscriptionsPause(payload)).unwrap();

      if (result?.status === 1) {
        toast.success(result?.message || "Subscription paused successfully!");
        setShowPauseModal(false);
        if (onUpdate) onUpdate();
      } else {
        toast.error(result?.message || "Failed to pause subscription");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to pause subscription");
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmCancel = async (reason) => {
    setIsSubmitting(true);
    const payload = {
      subscriptionId: subscription.id,
      reason: reason,
    };
    try {
      const result = await dispatch(subscriptionsCancel(payload)).unwrap();

      if (result?.status === 1) {
        toast.success(
          result?.message || "Subscription cancelled successfully!"
        );
        setShowCancelModal(false);
        if (onUpdate) onUpdate();
      } else {
        toast.error(result?.message || "Failed to cancel subscription");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to cancel subscription");
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmResume = async (selectedDate) => {
    setIsSubmitting(true);
    const payload = {
      subscriptionId: subscription.id,
      next_delivery_date: selectedDate,
    };
    try {
      const result = await dispatch(subscriptionsResume(payload)).unwrap();

      if (result?.status === 1) {
        toast.success(result?.message || "Subscription resumed successfully!");
        setShowResumeModal(false);
        if (onUpdate) onUpdate();
      } else {
        toast.error(result?.message || "Failed to resume subscription");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to resume subscription");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white transition-all duration-300 border rounded-lg border-gray-200 shadow-md hover:shadow-xl overflow-hidden m-4 mb-0 last:mb-5">
      {/* Main Content Section */}
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Mobile: Full Width Layout */}
          <div className="md:hidden space-y-4">
            {/* Mobile Header: Image, Title, Status */}
            <div className="flex gap-3">
              <img
                src={subscription.item?.product_image || noproducts}
                alt={subscription.subscription_name}
                className="w-20 h-20 object-cover rounded-lg border border-gray-200 bg-gray-100 flex-shrink-0"
                onError={(e) => {
                  e.target.src = noproducts;
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewDetails();
                }}
              />
              <div className="flex-1">
                <h3 className="text-base font-bold text-gray-800 leading-tight mb-2 line-clamp-2">
                  {subscription.subscription_name}
                </h3>
                <div
                  className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full border-2 ${
                    subscription.status === "active"
                      ? "bg-green-50 border-green-500"
                      : "bg-gray-50 border-gray-500"
                  }`}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${
                      subscription.status === "active"
                        ? "bg-green-500 animate-pulse"
                        : "bg-gray-500"
                    }`}
                  ></div>
                  <span
                    className={`text-xs font-bold capitalize ${
                      subscription.status === "active"
                        ? "text-green-700"
                        : "text-gray-700"
                    }`}
                  >
                    {subscription.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile: Delivery Frequency & Price */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary-100" />
                <span className="text-sm font-semibold text-primary-100 capitalize">
                  {subscription?.frequency_details?.description} Delivery
                </span>
              </div>
              <div className="text-primary-100 font-bold text-lg">
                {subscription.formatted_total}
              </div>
            </div>

            {/* Mobile: Order Type Badge */}
            {subscription?.order_type && (
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700 font-medium">
                  {formatOrderType(subscription.order_type)}
                </span>
              </div>
            )}

            {/* Mobile: Address */}
            {subscription?.delivery_address && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-primary-100 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  {subscription.delivery_address.name && (
                    <span className="text-red-600 font-semibold capitalize">
                      {subscription.delivery_address.name}
                    </span>
                  )}
                  {subscription.delivery_address.address && (
                    <span className="text-gray-700">
                      {subscription.delivery_address.name && ", "}
                      {subscription.delivery_address.address}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Mobile: Next Delivery & Orders */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary-100" />
                <div className="text-sm">
                  <span className="text-gray-500 text-xs">Next: </span>
                  <span className="text-gray-700 font-medium">
                    {new Date(
                      subscription.next_delivery_date
                    ).toLocaleDateString("en-US", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile: Action Buttons */}
            {subscription.status === "cancelled" ? (
              <div className="flex items-center gap-2 text-red-600 font-semibold justify-center py-2">
                <Ban className="w-5 h-5" />
                <span className="text-sm">This subscription was cancelled</span>
              </div>
            ) : (
              <div className="flex gap-2">
                {subscription.status === "paused" ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResumeClick();
                    }}
                    className="flex-1 bg-white border-2 border-green-500 text-green-600 font-semibold py-2 px-2 rounded-lg hover:bg-green-50 transition-all duration-200 flex items-center justify-center gap-1 text-xs sm:text-sm"
                  >
                    <Play className="w-3 h-3 sm:w-4 sm:h-4 -mt-0.5" />
                    <span>Resume</span>
                  </button>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePauseClick();
                    }}
                    className="flex-1 bg-white border-2 border-gray-300 text-gray-600 font-semibold py-2 px-2 rounded-lg hover:bg-gray-50 transition-all duration-200 flex items-center justify-center gap-1 text-xs sm:text-sm"
                  >
                    <Pause className="w-3 h-3 sm:w-4 sm:h-4 -mt-0.5" />
                    <span>Pause</span>
                  </button>
                )}

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelClick();
                  }}
                  className="flex-1 bg-white border-2 border-gray-300 text-gray-600 font-semibold py-2 px-2 rounded-lg hover:bg-gray-50 transition-all duration-200 flex items-center justify-center gap-1 text-xs sm:text-sm"
                >
                  <X className="w-3 h-3 sm:w-4 sm:h-4 -mt-0.5" />
                  <span>Cancel</span>
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetails();
                  }}
                  className="flex-1 bg-primary-100 text-white font-semibold py-2 px-2 rounded-lg hover:bg-red-700 transition-all duration-200 flex items-center justify-center gap-1 text-xs sm:text-sm"
                >
                  <span>View</span>
                  <ChevronsRight className="w-3 h-3 sm:w-4 sm:h-4 -mt-0.5" />
                </button>
              </div>
            )}
          </div>

          {/* Desktop: Original 3-Column Layout */}
          {/* Column 1: Product Image (30%) */}
          <div className="hidden md:block md:col-span-3">
            <img
              src={subscription.item?.product_image || noproducts}
              alt={subscription.subscription_name}
              className="w-full h-40 object-cover rounded-lg border border-gray-200 bg-gray-100 cursor-pointer"
              onError={(e) => {
                e.target.src = noproducts;
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails();
              }}
            />
          </div>

          {/* Column 2: Product Details (45%) */}
          <div className="hidden md:block md:col-span-7">
            {/* Product Title */}
            <h3
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails();
              }}
              className="text-lg font-bold text-gray-800 leading-tight mb-1 line-clamp-2 cursor-pointer"
            >
              {subscription.subscription_name}
            </h3>

            {/* Delivery Frequency */}
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-primary-100" />
              <span className="text-sm font-semibold text-primary-100 capitalize">
                {subscription?.frequency_details?.description} Delivery
              </span>
            </div>

            {/* Order Type Badge */}
            {subscription?.order_type && (
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-4 h-4 text-primary-100" />
                <span className="text-sm text-gray-700 font-medium">
                  Type : {formatOrderType(subscription.order_type)}
                </span>
              </div>
            )}

            <div className="space-y-2">
              {/* Info Section */}
              {subscription?.delivery_address && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary-100" />
                  <div className="text-sm">
                    {subscription.delivery_address.name && (
                      <span className="text-red-600 font-semibold capitalize">
                        {subscription.delivery_address.name}
                      </span>
                    )}
                    {subscription.delivery_address.address && (
                      <span className="text-gray-700">
                        {subscription.delivery_address.name && ", "}
                        {subscription.delivery_address.address}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {/* Next and Orders in Same Line */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-primary-100" />
                  <span className="text-sm text-gray-700">
                    Next Delivery :{" "}
                    {new Date(
                      subscription.next_delivery_date
                    ).toLocaleDateString("en-US", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Column 3: Status, Price, Buttons (25%) */}
          <div className="hidden md:flex md:col-span-2 flex-col justify-between items-end">
            <div className="flex flex-col items-end gap-3">
              {/* Active Status Indicator */}
              <div
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full border-2 ${
                  subscription.status === "active"
                    ? "bg-green-50 border-green-500"
                    : "bg-gray-50 border-gray-500"
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full ${
                    subscription.status === "active"
                      ? "bg-green-500 animate-pulse"
                      : "bg-gray-500"
                  }`}
                ></div>
                <span
                  className={`text-sm font-bold capitalize ${
                    subscription.status === "active"
                      ? "text-green-700"
                      : "text-gray-700"
                  }`}
                >
                  {subscription.status}
                </span>
              </div>

              {/* Price */}
              <div className="flex items-center gap-1 text-primary-100 font-bold text-xl">
                <span>{subscription.formatted_total}</span>
              </div>
            </div>

            {/* Action Buttons - Bottom Right */}
            {subscription.status === "cancelled" ? (
              <div className="flex items-center gap-2 text-red-600 font-semibold">
                <Ban className="w-5 h-5" />
                <span>Cancelled</span>
              </div>
            ) : (
              <div className="flex gap-2">
                {subscription.status === "paused" ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResumeClick();
                    }}
                    className="bg-white border-2 border-green-500 text-green-600 font-semibold py-1.5 px-2 rounded-md hover:bg-green-50 transition-all duration-200 flex items-center justify-center gap-1.5 text-sm"
                  >
                    <Play className="w-4 h-4 -mt-1" />
                    Resume
                  </button>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePauseClick();
                    }}
                    className="bg-white border-2 border-gray-300 text-gray-600 font-semibold py-1.5 px-2 rounded-md hover:bg-gray-50 transition-all duration-200 flex items-center justify-center gap-1.5 text-sm"
                  >
                    <Pause className="w-4 h-4 -mt-1" />
                    Pause
                  </button>
                )}

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelClick();
                  }}
                  className="bg-white border-2 border-gray-300 text-gray-600 font-semibold py-1.5 px-2 rounded-md hover:bg-gray-50 transition-all duration-200 flex items-center justify-center gap-1.5 text-sm"
                >
                  <X className="w-4 h-4 -mt-1" />
                  Cancel
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetails();
                  }}
                  className="bg-primary-100 text-white font-semibold py-1.5 px-3 rounded-md hover:bg-red-700 transition-all duration-200 flex items-center justify-center gap-1.5 text-sm"
                >
                  View <ChevronsRight className="w-4 h-4 -mt-1" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <PauseSubscriptionModal
        isOpen={showPauseModal}
        onClose={() => setShowPauseModal(false)}
        onConfirm={confirmPause}
        subscriptionName={subscription.subscription_name}
        schedules={subscription.schedules}
        isSubmitting={isSubmitting}
      />

      <ResumeSubscriptionModal
        isOpen={showResumeModal}
        onClose={() => setShowResumeModal(false)}
        onConfirm={confirmResume}
        subscriptionName={subscription.subscription_name}
        schedules={subscription.schedules}
        isSubmitting={isSubmitting}
      />

      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={confirmCancel}
        subscriptionName={subscription.subscription_name}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};
